// Client side checks of satellite routers, mirrors RoutesValidator, SystemRoutesValidator and SatelliteRouterService on
// the backend.
// The backend validates again (and checks that the landings and the fixed platforms belong to the user).

export const ROUTER_NAME_MAX_LENGTH = 255;

export const PLATFORM_FIXED = "fixed";
export const PLATFORM_PARAM = "param";

export const SUB_PARAMS_COUNT = 4;

// Lowercase segments of latin letters, digits, ".", "_" and "-", no trailing slash.
export const PATH_PATTERN = /^\/(?:[a-z0-9._-]+(?:\/[a-z0-9._-]+)*)?$/;

// "." and ".." segments: browsers resolve them, so such a route is never requested.
const DOT_SEGMENT_PATTERN = /\/\.\.?(?:\/|$)/;

// Paths starting with "/_" (/_c/..., /_subscribe) or "/.well-known" (ACME challenges): the agent answers them itself on
// every host.
const RESERVED_PREFIX_PATTERN = /^\/(?:_|\.well-known(?:\/|$))/;

// Paths the agent answers itself on every host.
export const RESERVED_PATHS = ["/healthz"];

export const PARAM_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

// System routes: scripts every site of the router serves. Service worker paths can only be added: push subscriptions are
// bound to the worker URL. The monetization script has exactly one path that can be changed.
export const SYSTEM_ROUTE_SERVICE_WORKER = "service_worker";
export const SYSTEM_ROUTE_MONETIZATION = "monetization";

// Used until /satellite-routers/options is loaded or if it lacks a value; the same as the backend defaults.
export const DEFAULT_ROUTER_OPTIONS = {
  defaults: {
    platform_param: "s",
    sub_params: ["p1", "p2", "p3", "p4"],
    strategy: "random",
    system_routes: [
      { type: SYSTEM_ROUTE_SERVICE_WORKER, path: "/sw.js" },
      { type: SYSTEM_ROUTE_MONETIZATION, path: "/lib.js" },
    ],
  },
  limits: { routes: 50, landings_per_route: 10, path_length: 128, service_worker_paths: 10 },
};

export const STRATEGIES = ["random"];

// Path as the backend stores it: lowercase, without the trailing slash (except the root).
export function normalizePath(path) {
  const value = String(path || "")
    .trim()
    .toLowerCase();
  return value === "/" ? value : value.replace(/\/+$/, "");
}

// Each validator returns [translation key, params] of the first error, or null if the value is valid.
// An error with `required: true` in the params is about a missing value: the editor shows it after a save attempt only.

export function validateRouterName(name) {
  const value = String(name || "").trim();
  if (!value) return ["satellites.routers.errors.name_required", { required: true }];
  if (value.length > ROUTER_NAME_MAX_LENGTH) return ["satellites.routers.errors.name_length", { number: ROUTER_NAME_MAX_LENGTH }];
  return null;
}

// takenPaths: normalized paths of the previous routes of the router.
export function validatePath(path, maxLength, takenPaths = []) {
  if (!String(path || "").trim()) return ["satellites.routers.errors.path_required", { required: true }];
  const value = normalizePath(path);
  if (value.length > maxLength) return ["satellites.routers.errors.path_length", { number: maxLength }];
  if (!PATH_PATTERN.test(value)) return ["satellites.routers.errors.path_format"];
  if (DOT_SEGMENT_PATTERN.test(value)) return ["satellites.routers.errors.path_dot_segment"];
  if (RESERVED_PREFIX_PATTERN.test(value)) return ["satellites.routers.errors.path_reserved_prefix", { path: value }];
  if (RESERVED_PATHS.includes(value)) return ["satellites.routers.errors.path_reserved", { path: value }];
  if (takenPaths.includes(value)) return ["satellites.routers.errors.path_duplicate", { path: value }];
  return null;
}

export function validatePlatform(platform) {
  if (platform.mode === PLATFORM_FIXED) {
    return Number.isInteger(platform.id) && platform.id > 0 ? null : ["satellites.routers.errors.platform_required", { required: true }];
  }
  return PARAM_PATTERN.test(platform.param) ? null : ["satellites.routers.errors.param_format"];
}

// Returns { error, slots }: slots are the indexes of the sub parameters to highlight.
export function validateSubParams(subParams, platform) {
  const seen = {};
  if (platform.mode === PLATFORM_PARAM && platform.param) seen[platform.param] = -1;
  for (let i = 0; i < subParams.length; i++) {
    const name = subParams[i];
    if (name === "") continue;
    if (!PARAM_PATTERN.test(name)) return { error: ["satellites.routers.errors.param_format"], slots: [i] };
    if (name in seen) {
      return { error: ["satellites.routers.errors.sub_duplicate", { name }], slots: seen[name] >= 0 ? [seen[name], i] : [i] };
    }
    seen[name] = i;
  }
  return { error: null, slots: [] };
}

export function validateLandingIds(ids, max) {
  if (!ids.length) return ["satellites.routers.errors.landings_required", { required: true }];
  if (ids.length > max) return ["satellites.routers.errors.landings_max", { number: max }];
  return null;
}

// routePaths: normalized paths of the routes; takenPaths: normalized paths of the previous system routes.
export function validateSystemPath(path, maxLength, routePaths = [], takenPaths = []) {
  const error = validatePath(path, maxLength, takenPaths);
  if (error) return error;
  const value = normalizePath(path);
  if (!value.endsWith(".js")) return ["satellites.routers.errors.system_path_js"];
  if (routePaths.includes(value)) return ["satellites.routers.errors.system_path_route", { path: value }];
  return null;
}
